import { GenerationSkeleton } from '@components/generation/generation.skeleton';
import {
  GenerationList,
  GenerationRow
} from '@components/generation/generation-row';
import { CancelProgress } from '@components/progress/cancelProgress';
import { DoneProgress } from '@components/progress/doneProgress';
import { TodoProgress } from '@components/progress/todoProgress';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '@components/ui/pagination';
import {
  SettingHeader,
  SettingsContainer,
  SettingsContent
} from '@components/ui/settings';
import useProcess from '@hooks/useProcess';
import useStores from '@hooks/useStore';
import type {
  GenerationListItem,
  GenerationStatus
} from '@stratumn/studio-shared';
import {
  Check,
  Loader2,
  LoaderCircle,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EmptyBoxIcon } from '@/components/icons/EmptyBoxIcon';
import { Button } from '@/components/ui/button';
import { DynamicButton } from '@/components/ui/dynamic-button';
import { useGenerations } from '@/hooks/queries/generations/useGenerations';
import { GenerationFilterBar } from './filters/GenerationFilterBar';
import { useGenerationFilters } from './filters/useGenerationFilters';

export const generationStatusToProgress = (
  status: GenerationListItem['status'] | GenerationStatus,
  size = 14
) => {
  const normalized = status.toUpperCase();
  switch (normalized) {
    case 'RUNNING':
      return <Loader2 className="h-4 w-4 animate-spin" color="#4b35d2" />;
    case 'FAILED':
      return <CancelProgress color="#eb5757" size={size} />;
    case 'READY':
      return <DoneProgress color="#28a745" size={size} />;
    case 'PENDING':
      return <TodoProgress size={size} />;
    case 'TIMEOUT':
      return <CancelProgress color="#f2bf00" size={size} />;
    default:
      return <TodoProgress size={size} />;
  }
};

export const POLL_INTERVAL = 5000;
export const MAX_NAME_LENGTH = 10;

const SYNC_STATES = {
  idle: {
    label: 'Sync',
    icon: RefreshCw,
    iconClassName: 'group-hover:rotate-90'
  },
  syncing: { label: 'Syncing', icon: LoaderCircle, iconSpin: true },
  synced: { label: 'Synced', icon: Check }
} as const;

export const MIN_SYNCING_DURATION = 2000;
export const SYNCED_DISPLAY_DURATION = 2500;

export const SyncButton = ({
  isFetching,
  onSync
}: {
  isFetching: boolean;
  onSync: () => void;
}) => {
  const [syncState, setSyncState] = useState<'idle' | 'syncing' | 'synced'>(
    'idle'
  );
  const syncStartedAt = useRef<number>(0);

  const handleSync = useCallback(() => {
    setSyncState('syncing');
    syncStartedAt.current = Date.now();
    onSync();
  }, [onSync]);

  // Transition: syncing → synced (after min duration) → idle
  useEffect(() => {
    if (syncState === 'syncing' && !isFetching) {
      const elapsed = Date.now() - syncStartedAt.current;
      const remaining = Math.max(0, MIN_SYNCING_DURATION - elapsed);

      const timeout = setTimeout(() => {
        setSyncState('synced');
      }, remaining);
      return () => clearTimeout(timeout);
    }

    if (syncState === 'synced') {
      const timeout = setTimeout(() => {
        setSyncState('idle');
      }, SYNCED_DISPLAY_DURATION);
      return () => clearTimeout(timeout);
    }

    return;
  }, [isFetching, syncState]);

  return (
    <DynamicButton
      current={syncState}
      onClick={syncState !== 'syncing' ? handleSync : undefined}
      states={SYNC_STATES}
      variant="outline"
    />
  );
};

export const formatDuration = (seconds: number | null): string => {
  if (seconds === null) {
    return '—';
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m`;
  }
  return `${Math.floor(seconds / 3600)}h`;
};

const TIME_UNITS = [
  { label: 'year', seconds: 31_536_000 },
  { label: 'day', seconds: 86_400 },
  { label: 'hour', seconds: 3600 },
  { label: 'minute', seconds: 60 },
  { label: 'second', seconds: 1 }
] as const;

const formatUnit = (value: number, label: string): string =>
  `${value} ${label}${value === 1 ? '' : 's'}`;

export const formatDurationLong = (seconds: number | null): string => {
  if (seconds === null) {
    return '—';
  }
  for (const unit of TIME_UNITS) {
    if (seconds >= unit.seconds) {
      const value = Math.floor(seconds / unit.seconds);
      return formatUnit(value, unit.label);
    }
  }
  return formatUnit(0, 'second');
};

export const formatElapsedTime = (createdAt: string): string => {
  const seconds = Math.floor(
    (Date.now() - new Date(createdAt).getTime()) / 1000
  );

  for (const unit of TIME_UNITS) {
    if (seconds >= unit.seconds) {
      const value = Math.floor(seconds / unit.seconds);
      return `${formatUnit(value, unit.label)} ago`;
    }
  }

  return '0 seconds ago';
};

export const getSourceTag = (source: string): string => {
  const parts = source.split(':');
  return parts.at(-1);
};

export const getPageSlots = (
  current: number,
  total: number
): (number | '...')[] => {
  if (total <= 6) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  // Always show a window of 3 consecutive pages, clamped to [1, total]
  const windowStart = Math.max(1, Math.min(current - 1, total - 2));
  const windowEnd = Math.min(total, windowStart + 2);
  const window = Array.from(
    { length: windowEnd - windowStart + 1 },
    (_, i) => windowStart + i
  );

  const result: (number | '...')[] = [];

  if (window[0] !== 1) {
    result.push(1);
    if (window[0] > 2) {
      result.push('...');
    }
  }

  result.push(...window);

  if (window.at(-1) !== total) {
    if (window.at(-1) < total - 1) {
      result.push('...');
    }
    result.push(total);
  }

  return result;
};

const Generation = () => {
  const process = useProcess();
  const [searchParams] = useSearchParams();
  const { workspaceStore } = useStores();
  const navigate = useNavigate();
  const { filters, hasActiveFilters } = useGenerationFilters();

  // Ensure workspaces are loaded (for the filter picker and colors)
  useEffect(() => {
    if (process) {
      const workspaces = workspaceStore.getWorkspacesForProcess(process.id);
      if (workspaces.length === 0 && !workspaceStore.isLoading(process.id)) {
        void workspaceStore.fetchWorkspaces(process.id);
      }
    }
  }, [process, workspaceStore]);

  const computeCurrentPage = () => {
    const currentPageParam = searchParams.get('page');
    if (!currentPageParam) {
      return 1;
    }
    const page = Number.parseInt(currentPageParam, 10);
    if (Number.isNaN(page) || page < 1) {
      return 1;
    }
    return page;
  };

  const currentPage = computeCurrentPage();

  const filtersForApi = hasActiveFilters ? filters : undefined;

  const {
    generations,
    isLoading,
    isFetching,
    isError,
    numberOfPages,
    refetch
  } = useGenerations({
    processId: process?.id ?? '',
    page: currentPage,
    filters: filtersForApi,
    enabled: Boolean(process?.id)
  });

  /**
   * If the URL points at a page beyond the available range (e.g. after
   * a filter narrows results, or a stale bookmark), clamp it back to the
   * last page so the user sees actual results instead of an empty state.
   */
  useEffect(() => {
    if (numberOfPages > 0 && currentPage > numberOfPages) {
      const next = new URLSearchParams(searchParams);
      next.set('page', String(numberOfPages));
      navigate(`?${next.toString()}`, { replace: true });
    }
  }, [numberOfPages, currentPage, navigate, searchParams]);

  if (!process || process.isSmallProcess) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const workspaces = workspaceStore.getWorkspacesForProcess(process.id);
  const collaborators = process.collaborators ?? [];

  /**
   * Build search params for pagination links, preserving current filters.
   */
  const buildPageParams = (page: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(page));
    return `?${next.toString()}`;
  };

  return (
    <SettingsContainer>
      <SettingHeader
        description="See previous and ongoing generations"
        title="Generations"
      />
      <SettingsContent className="gap-4">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <GenerationFilterBar
              collaborators={collaborators}
              workspaces={workspaces}
            />
          </div>
          <div className="flex items-center gap-2">
            <SyncButton isFetching={isFetching} onSync={refetch} />
            <Button onClick={() => navigate('new')}>
              <Sparkles className="mr-2 h-4 w-4" />
              New Generation
            </Button>
          </div>
        </div>

        <div>
          {isLoading &&
            Array.from({ length: 7 }).map((_, i) => (
              <GenerationSkeleton
                index={i}
                isFirst={i === 0}
                isLast={i === 6}
                key={i}
              />
            ))}

          {!isLoading && isError && (
            <div className="flex flex-col items-center gap-3 py-12">
              <p className="text-center text-muted-foreground text-sm">
                Couldn't load generations
              </p>
              <Button onClick={() => refetch()} variant="outline">
                Retry
              </Button>
            </div>
          )}

          {!(isLoading || isError) && generations.length > 0 && (
            <GenerationList>
              {generations.map((generation, index) => (
                <GenerationRow
                  generation={generation}
                  isFirst={index === 0}
                  isLast={index === generations.length - 1}
                  key={generation.id}
                  to={generation.id}
                  workspaceColor={
                    workspaces.find(w => w.id === generation.workspaceId)?.color
                  }
                />
              ))}
            </GenerationList>
          )}

          {!(isLoading || isError) && generations.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-12">
              <EmptyBoxIcon className="size-16" />
              <p className="text-center text-muted-foreground text-sm">
                This process has not been generated yet
              </p>
            </div>
          )}
        </div>

        {numberOfPages > 1 && (
          <Pagination className="mt-3">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => {
                    if (currentPage > 1) {
                      navigate(buildPageParams(currentPage - 1));
                    }
                  }}
                />
              </PaginationItem>

              {getPageSlots(currentPage, numberOfPages).map((slot, index) => {
                if (slot === '...') {
                  return (
                    <PaginationItem key={`ellipsis-${index}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }

                return (
                  <PaginationItem key={slot}>
                    <PaginationLink
                      isActive={slot === currentPage}
                      onClick={() => navigate(buildPageParams(slot))}
                    >
                      {slot}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  onClick={() => {
                    if (currentPage < numberOfPages) {
                      navigate(buildPageParams(currentPage + 1));
                    }
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </SettingsContent>
    </SettingsContainer>
  );
};

export const GenerationPage = observer(Generation);
